import React, { useEffect, useState, useCallback, useMemo, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw, ChevronLeft, ChevronRight, ChevronDown, Download, Loader2, ClipboardList } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import {
  PAGE_WIDE, SearchInput, Switch, PersonAvatar, EmptyState, ErrorState, TableSkeleton,
  fmtAgo, fmtDate, fmtDayMonth, fmtTime, humanise, num, plural,
} from '../../components/ui';

// Activity log: who changed what, and when — written for people rather than servers. Failures read
// "Tried to …" with a Failed badge; the HTTP method, path and status sit in each row's details.
// Automatic entries (AI insight refreshes, read receipts) are hidden unless asked for.

const ENTITY_TYPES = [
  '', 'shift', 'client', 'staff', 'timesheet', 'invoice', 'leave request',
  'incident', 'medication', 'task', 'service agreement', 'payslip', 'note',
  'document', 'role', 'integration', 'settings',
];
// Written automatically by the app, not by a person doing something: the dashboard's AI insight
// refresh (POST /ai/insights) and "seen" / "read" markers.
const SYSTEM_TYPES = ['insight', 'seen', 'read'];
const LIMIT = 50;

// Record → where to open it. Client and staff sub-records open that client or staff member;
// other records open their module.
const MODULE_PAGE = {
  incident: ['/incidents', 'Incidents'], invoice: ['/invoices', 'Invoices'], task: ['/tasks', 'Tasks'],
  'service agreement': ['/service-agreements', 'Service agreements'], medication: ['/medications', 'Medications'],
  vehicle: ['/vehicles', 'Fleet'], facility: ['/facilities', 'Facilities'], timesheet: ['/timesheets', 'Timesheets'],
  'leave request': ['/leave', 'Leave'], shift: ['/scheduler', 'Scheduler'], form: ['/forms', 'Forms'],
  payslip: ['/payslips', 'Payslips'], automation: ['/automations', 'Reminders & reviews'],
};
const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
function recordLink(r) {
  const path = String(r.path || '');
  const client = new RegExp(`^/api/clients/(${UUID})(/|$)`, 'i').exec(path);
  const staff = new RegExp(`^/api/(?:staff|users)/(${UUID})(/|$)`, 'i').exec(path);
  const deletedSelf = r.method === 'DELETE' && !path.replace(/\/+$/, '').split('/').slice(4).length;
  if (client && !(deletedSelf && r.entity_type === 'client')) return { to: `/clients/${client[1]}`, title: 'Open this client' };
  if (staff && !(deletedSelf && r.entity_type === 'staff')) return { to: `/staff/${staff[1]}`, title: 'Open this staff member' };
  const mod = MODULE_PAGE[r.entity_type];
  if (mod && r.method !== 'DELETE') return { to: mod[0], title: `Open ${mod[1]}` };
  return null;
}

const failed = (r) => Number(r.status) >= 400;
// "Created document" → "Tried to create document" when the request failed.
function actionText(r) {
  const a = String(r.action || humanise(r.entity_type) || 'Action');
  if (!failed(r)) return a;
  const m = /^(Created|Updated|Deleted)\s+(.*)$/.exec(a);
  if (m) return `Tried to ${({ Created: 'create', Updated: 'update', Deleted: 'delete' })[m[1]]} ${m[2]}`;
  return `Tried to ${a.charAt(0).toLowerCase()}${a.slice(1)}`;
}

// created_epoch is the true instant (created_at is read ~10h early); fall back against an older API.
const instantOf = (r) => (r.created_epoch != null ? Number(r.created_epoch) * 1000 : new Date(r.created_at).getTime());
const startOfDay = (ts) => { const d = new Date(ts); d.setHours(0, 0, 0, 0); return d.getTime(); };
function dayLabel(ts) {
  const diff = Math.round((startOfDay(Date.now()) - startOfDay(ts)) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  return new Date(ts).getFullYear() === new Date().getFullYear() ? fmtDayMonth(new Date(ts)) : fmtDate(new Date(ts));
}
const clockOf = (ts) => { const d = new Date(ts); return fmtTime(`${d.getHours()}:${d.getMinutes()}`); };
const whenText = (ts) => `${fmtDayMonth(new Date(ts))}, ${clockOf(ts)}`;

// Day dividers, then runs of the same action by the same person folded into one row.
function buildFeed(rows) {
  const days = [];
  rows.forEach((r) => {
    const ts = instantOf(r);
    const key = startOfDay(ts);
    let day = days[days.length - 1];
    if (!day || day.key !== key) { day = { key, label: dayLabel(ts), groups: [] }; days.push(day); }
    const sig = `${r.user_id || r.user_name}|${r.action}|${r.entity_type}|${failed(r) ? 1 : 0}`;
    const g = day.groups[day.groups.length - 1];
    if (g && g.sig === sig) g.items.push(r);
    else day.groups.push({ sig, id: r.id, items: [r] });
  });
  return days;
}

function RecordCell({ r, count }) {
  // A folded run links only when every entry opens the same place (a module page).
  const own = recordLink(r);
  const link = count === 1 ? own : (own && !/^\/(clients|staff)\//.test(own.to) ? own : null);
  const label = humanise(r.entity_type) || 'Record';
  const id = count === 1 && r.entity_id ? ` #${String(r.entity_id).slice(0, 8)}` : '';
  const text = <>{label}<span className="text-slate-500 tabular-nums">{id}</span></>;
  return link
    ? <Link to={link.to} title={link.title} onClick={(e) => e.stopPropagation()} className="text-primary-700 hover:underline underline-offset-2">{text}</Link>
    : <span className="text-slate-700">{text}</span>;
}

function DetailList({ r }) {
  const summary = r.summary && typeof r.summary === 'object' ? Object.entries(r.summary) : [];
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 text-[12.5px]">
      <dl className="space-y-1">
        <div className="flex gap-2"><dt className="w-24 shrink-0 text-slate-500">Request</dt><dd className="font-mono text-slate-800 break-all">{r.method} {r.path}</dd></div>
        <div className="flex gap-2"><dt className="w-24 shrink-0 text-slate-500">HTTP status</dt><dd className={clsx('tabular-nums', failed(r) ? 'text-rose-700 font-medium' : 'text-slate-800')}>{r.status ?? '—'}</dd></div>
        {r.entity_id && <div className="flex gap-2"><dt className="w-24 shrink-0 text-slate-500">Record id</dt><dd className="font-mono text-slate-800 break-all">{r.entity_id}</dd></div>}
        {r.user_email && <div className="flex gap-2"><dt className="w-24 shrink-0 text-slate-500">Email</dt><dd className="text-slate-800 break-all">{r.user_email}</dd></div>}
        <div className="flex gap-2"><dt className="w-24 shrink-0 text-slate-500">Exact time</dt><dd className="text-slate-800">{fmtDate(new Date(instantOf(r)))}, {clockOf(instantOf(r))}</dd></div>
      </dl>
      <div>
        <p className="text-slate-500 mb-1">Fields sent</p>
        {summary.length ? (
          <dl className="rounded-lg border border-slate-200 divide-y divide-slate-100 bg-white">
            {summary.slice(0, 30).map(([k, v]) => (
              <div key={k} className="flex gap-3 px-3 py-1.5">
                <dt className="w-36 shrink-0 text-slate-500 truncate" title={k}>{humanise(k)}</dt>
                <dd className="text-slate-800 break-all">{v === '***' ? <span className="text-slate-500">Hidden for privacy</span> : v == null || v === '' ? '—' : String(v)}</dd>
              </div>
            ))}
          </dl>
        ) : <p className="text-slate-500">Not recorded for this action (health and personal records never store their contents here).</p>}
      </div>
    </div>
  );
}

const TH = 'py-2.5 px-3 text-left text-[12px] font-semibold text-slate-500 whitespace-nowrap';

export default function ActivityLog() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [people, setPeople] = useState([]);
  const [userId, setUserId] = useState('');
  const [entityType, setEntityType] = useState('');
  const [qDraft, setQDraft] = useState('');
  const [q, setQ] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [showSystem, setShowSystem] = useState(false);
  const [open, setOpen] = useState({});
  const [exporting, setExporting] = useState(false);
  // An audit log that renders "No activity" when its own query failed is the one screen that must
  // never do that: a reader can't tell an empty period from a broken one.
  const [loadError, setLoadError] = useState(null);

  useEffect(() => { const t = setTimeout(() => setQ(qDraft.trim()), 300); return () => clearTimeout(t); }, [qDraft]);

  const filterParams = useCallback(() => {
    const params = {};
    if (userId) params.userId = userId;
    if (entityType) params.entityType = entityType;
    if (q) params.q = q;
    if (from) params.from = from;
    if (to) params.to = `${to}T23:59:59`;
    if (!showSystem) params.excludeTypes = SYSTEM_TYPES.join(',');
    return params;
  }, [userId, entityType, q, from, to, showSystem]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/activity', { params: { page, limit: LIMIT, ...filterParams() } });
      setRows(data.rows || []); setTotal(data.total || 0); setLoadError(null); setOpen({});
    } catch (e) {
      setRows([]); setTotal(0);
      setLoadError((e && e.response && e.response.data && e.response.data.message) || "We couldn't load the activity log. Check your connection, then try again.");
    }
    setLoading(false);
  }, [page, filterParams]);

  const loadPeople = useCallback(async () => {
    try {
      const { data } = await api.get('/activity/people', { params: showSystem ? {} : { excludeTypes: SYSTEM_TYPES.join(',') } });
      setPeople(data.rows || []);
    } catch { /* the person shortcuts are optional */ }
  }, [showSystem]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadPeople(); }, [loadPeople]);
  useEffect(() => { setPage(1); }, [userId, entityType, q, from, to, showSystem]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      let tz = 'UTC';
      try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { /* keep UTC */ }
      const res = await api.get('/activity/export', { params: { ...filterParams(), tz }, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `activity-log-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(total > 20000 ? 'Exported the newest 20,000 entries' : `Exported ${plural(total, 'entry', 'entries')}`);
    } catch (e) {
      toast.error((e && e.response && e.response.data && e.response.data.message) || 'Could not export the activity log');
    } finally { setExporting(false); }
  };

  const pages = Math.max(1, Math.ceil(total / LIMIT));
  const clearFilters = () => { setUserId(''); setEntityType(''); setQDraft(''); setQ(''); setFrom(''); setTo(''); };
  const activeFilters = !!(userId || entityType || qDraft || from || to);
  const feed = useMemo(() => buildFeed(rows), [rows]);
  const toggle = (id) => setOpen((o) => ({ ...o, [id]: !o[id] }));

  return (
    <div className={PAGE_WIDE}>
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div className="flex flex-col gap-2.5 md:flex-row md:flex-wrap md:items-center min-w-0">
          <SearchInput value={qDraft} onChange={setQDraft} placeholder="Search action, person or path" className="w-full md:w-64" />
          <label htmlFor="activity-type" className="sr-only">Record type</label>
          <select id="activity-type" value={entityType} onChange={(e) => setEntityType(e.target.value)} className="input-field md:!w-44">
            {ENTITY_TYPES.map((t) => <option key={t} value={t}>{t ? humanise(t) : 'All record types'}</option>)}
          </select>
          <div className="flex items-center gap-2">
            <label htmlFor="activity-from" className="sr-only">From</label>
            <input id="activity-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="input-field md:!w-40" />
            <span className="text-slate-500 text-[13px]">to</span>
            <label htmlFor="activity-to" className="sr-only">To</label>
            <input id="activity-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="input-field md:!w-40" />
          </div>
          {activeFilters && <button type="button" onClick={clearFilters} className="btn-ghost btn-sm self-start md:self-auto">Clear filters</button>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="flex items-center gap-2 text-[13px] text-slate-700 mr-1 cursor-pointer" title="AI insight refreshes and read markers the app records by itself">
            <Switch checked={showSystem} onChange={setShowSystem} label="Show system events" />
            <span aria-hidden>Show system events</span>
          </label>
          <button type="button" onClick={() => { load(); loadPeople(); }} className="btn-secondary !px-2.5" aria-label="Refresh"><RefreshCw size={15} /></button>
          <button type="button" onClick={exportCsv} disabled={exporting || loading || !total} className="btn-secondary">
            {exporting ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Export CSV
          </button>
        </div>
      </div>

      {people.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-2" role="group" aria-label="Filter by person">
          {people.slice(0, 12).map((p) => {
            const on = userId && userId === p.user_id;
            const last = p.last_active_epoch != null ? Number(p.last_active_epoch) * 1000 : p.last_active;
            return (
              <button key={p.user_id || p.user_name} type="button" aria-pressed={!!on}
                onClick={() => setUserId(on ? '' : (p.user_id || ''))}
                className={clsx('flex items-center gap-2.5 text-left px-3 py-2 rounded-xl border transition-colors duration-150',
                  on ? 'border-primary-400 bg-primary-50' : 'border-slate-200 bg-white hover:border-slate-300')}>
                <PersonAvatar id={p.user_id} name={p.user_name} size="sm" />
                <span className="min-w-0">
                  <span className="block text-[13px] font-medium text-slate-900 truncate">{p.user_name}</span>
                  <span className="block text-[12px] text-slate-500 truncate tabular-nums">{plural(p.actions, 'action')} · {last ? fmtAgo(new Date(last)) : '—'}</span>
                </span>
              </button>
            );
          })}
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? <TableSkeleton rows={10} cols={5} />
          : loadError ? <ErrorState message={loadError} onRetry={load} />
          : rows.length === 0 ? (
            activeFilters
              ? <EmptyState icon={ClipboardList} title="No activity matches these filters" action={<button type="button" onClick={clearFilters} className="btn-secondary btn-sm">Clear filters</button>} />
              : <EmptyState icon={ClipboardList} title="No activity yet" hint="Every change people make, from shifts to settings, is recorded here with who made it and when." />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[13px] border-collapse">
                <caption className="sr-only">Activity, newest first</caption>
                <thead>
                  <tr className="border-b border-slate-200">
                    <th scope="col" className={TH}>When</th>
                    <th scope="col" className={TH}>Person</th>
                    <th scope="col" className={TH}>Action</th>
                    <th scope="col" className={TH}>Record</th>
                    <th scope="col" className={TH}>Result</th>
                    <th scope="col" className="w-12"><span className="sr-only">Details</span></th>
                  </tr>
                </thead>
                {feed.map((day) => (
                  <tbody key={day.key}>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th scope="rowgroup" colSpan={6} className="px-3 py-1.5 text-left text-[12px] font-semibold text-slate-600">{day.label}</th>
                    </tr>
                    {day.groups.map((g) => {
                      const r = g.items[0];
                      const n = g.items.length;
                      const first = instantOf(r);
                      const lastTs = instantOf(g.items[n - 1]);
                      const isOpen = !!open[g.id];
                      const detailId = `activity-detail-${g.id}`;
                      return (
                        <Fragment key={g.id}>
                          <tr className={clsx('border-b border-slate-100', isOpen ? 'bg-slate-50/70' : 'hover:bg-slate-50/60')}>
                            <td className="px-3 py-2 align-top whitespace-nowrap">
                              <div className="text-slate-800 tabular-nums">{n > 1 ? `${clockOf(lastTs)}–${clockOf(first)}` : whenText(first)}</div>
                              <div className="text-[12px] text-slate-500">{fmtAgo(new Date(first))}</div>
                            </td>
                            <td className="px-3 py-2 align-top">
                              <span className="text-slate-800 font-medium">{r.user_name || 'Unknown'}</span>
                              {r.user_role && <span className="text-slate-500"> · {humanise(r.user_role)}</span>}
                            </td>
                            <td className="px-3 py-2 align-top text-slate-800">
                              {actionText(r)}{n > 1 && <span className="ml-1 text-slate-500 tabular-nums">×{num(n)}</span>}
                            </td>
                            <td className="px-3 py-2 align-top whitespace-nowrap"><RecordCell r={r} count={n} /></td>
                            <td className="px-3 py-2 align-top">{failed(r) ? <span className="badge-red">Failed</span> : <span className="sr-only">Succeeded</span>}</td>
                            <td className="px-2 py-1.5 align-top text-right">
                              <button type="button" onClick={() => toggle(g.id)} aria-expanded={isOpen} aria-controls={detailId}
                                aria-label={isOpen ? 'Hide details' : 'Show details'}
                                className="p-2 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors duration-150">
                                <ChevronDown size={16} className={clsx('transition-transform duration-150', isOpen && 'rotate-180')} />
                              </button>
                            </td>
                          </tr>
                          {isOpen && (
                            <tr id={detailId} className="border-b border-slate-100 bg-slate-50/70">
                              <td colSpan={6} className="px-4 pb-4 pt-1">
                                {n === 1 ? <DetailList r={r} /> : (
                                  <ul className="rounded-lg border border-slate-200 bg-white divide-y divide-slate-100 text-[12.5px]">
                                    {g.items.map((it) => (
                                      <li key={it.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-1.5">
                                        <span className="tabular-nums text-slate-800 w-20">{clockOf(instantOf(it))}</span>
                                        <RecordCell r={it} count={1} />
                                        <span className="font-mono text-slate-500 break-all">{it.method} {it.path}</span>
                                        <span className={clsx('ml-auto tabular-nums', failed(it) ? 'text-rose-700' : 'text-slate-500')}>{it.status ?? '—'}</span>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                ))}
              </table>
            </div>
          )}

        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-t border-slate-100 text-[13px] text-slate-500">
          <span className="tabular-nums">{plural(total, 'entry', 'entries')}{!showSystem ? ' · system events hidden' : ''}</span>
          <div className="flex items-center gap-2">
            <button type="button" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)} aria-label="Previous page"
              className="p-2 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50"><ChevronLeft size={16} /></button>
            <span className="tabular-nums">Page {num(page)} of {num(pages)}</span>
            <button type="button" disabled={page >= pages || loading} onClick={() => setPage((p) => p + 1)} aria-label="Next page"
              className="p-2 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50"><ChevronRight size={16} /></button>
          </div>
        </div>
      </div>
    </div>
  );
}
